import React from 'react';
import { Chart as ChartJS, ArcElement, Tooltip } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import DashboardCard from './DashboardCard';
import { TOOLTIP } from './chartTheme';

ChartJS.register(ArcElement, Tooltip);

const GENDER_COLOURS = ['#8B173B', '#00999E', '#E08AA5', '#006366'];
const UNKNOWN_COLOUR = '#C4C4C4';

const humanize = (value) =>
  value.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

export function GenderDonut({ gender }) {
  const colours = gender.map((row, index) =>
    row.value === 'unknown'
      ? UNKNOWN_COLOUR
      : GENDER_COLOURS[index % GENDER_COLOURS.length]
  );

  const data = {
    labels: gender.map((row) => humanize(row.value)),
    datasets: [
      {
        data: gender.map((row) => row.count),
        backgroundColor: colours,
        borderColor: '#FFFFFF',
        borderWidth: 2,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '68%',
    plugins: {
      legend: { display: false },
      tooltip: {
        ...TOOLTIP,
        callbacks: {
          label: (item) => {
            const row = gender[item.dataIndex];
            return ` ${row.count.toLocaleString()} (${row.percent}%)`;
          },
        },
      },
    },
  };

  return (
    <DashboardCard title="Gender" subtitle="Live cohort members">
      <div className="flex items-center gap-6">
        <div className="h-36 w-36 shrink-0">
          <Doughnut data={data} options={options} />
        </div>
        <ul className="flex-1 space-y-3 list-none p-0 m-0">
          {gender.map((row, index) => (
            <li key={row.value} className="flex items-center gap-2 text-sm">
              <span
                className="h-2.5 w-2.5 rounded-sm shrink-0"
                style={{ backgroundColor: colours[index] }}
              />
              <span className="flex-1 text-gray-700">
                {humanize(row.value)}
              </span>
              <span className="font-semibold text-gray-900 tabular-nums">
                {row.percent}%
              </span>
              <span className="w-12 text-right text-xs text-gray-400 tabular-nums">
                {row.count.toLocaleString()}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </DashboardCard>
  );
}

// Plain bar rows rather than a chart: one country can dominate (Kenya is
// ~96%), and rows keep every figure readable beside its bar.
export function TopCountries({ topCountries, otherCountries, countryUnknown }) {
  const rows =
    otherCountries && otherCountries.count > 0
      ? [...topCountries, otherCountries]
      : topCountries;
  const maxCount = Math.max(...rows.map((row) => row.count), 1);

  return (
    <DashboardCard title="Top countries" subtitle="Live cohort members">
      <ul className="space-y-2 list-none p-0 m-0">
        {rows.map((row) => {
          const isOther = row === otherCountries;
          return (
            <li
              key={row.value}
              className="grid grid-cols-[7rem_1fr_5.5rem] items-center gap-3 text-sm"
            >
              <span
                className={`truncate ${
                  isOther ? 'text-gray-500' : 'text-gray-700'
                }`}
              >
                {row.value}
              </span>
              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${
                    isOther ? 'bg-gray-400' : 'bg-persian-500'
                  }`}
                  style={{
                    width: `${Math.max((row.count / maxCount) * 100, 1)}%`,
                  }}
                />
              </div>
              <span className="text-right tabular-nums text-xs text-gray-700">
                <span className="font-semibold">{row.percent}%</span>{' '}
                <span className="text-gray-400">
                  ({row.count.toLocaleString()})
                </span>
              </span>
            </li>
          );
        })}
      </ul>
      {countryUnknown > 0 && (
        <p className="text-xs text-gray-500 mt-3">
          {countryUnknown.toLocaleString()} members have no country set, so
          shares may add up to less than 100%.
        </p>
      )}
    </DashboardCard>
  );
}
